import { useNavigate } from 'react-router-dom';
import { Button, Badge, Alert } from '@dreamtree-org/twreact-ui';
import { PageHeader, StatCard, StatusBadge, SectionCard, QueryState, EmptyState } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { formatINR, formatDate } from '@/lib/format';
import DataTable from '../components/DataTable';
import { AreaChart, BarChart, DonutChart } from '@dreamtree-org/twreact-ui/charts';
import { shares } from '../lib/metrics';
import { formatDateTime, formatINRCompact, humanize } from '../lib/format';
import { usePlatformCan } from '../lib/access';

const HEALTH_VARIANT = { ok: 'success', warning: 'warning', error: 'error' };

/**
 * Platform dashboard: merchant counts by status/plan, billed MRR, 6-month GMV and
 * orders across every store, recent audit events (audit.view) and health tiles
 * (all from /api/platform/overview, cached for a minute on the server).
 */
export default function Dashboard() {
  const nav = useNavigate();
  const stats = useQuery('platformStats', undefined, { select: (r) => r.data });
  const merchants = useQuery('platformMerchants', undefined, { select: (r) => r.data });
  const overview = useQuery('platformOverview', undefined, { select: (r) => r.data });
  const canAudit = usePlatformCan('audit.view');
  const canManage = usePlatformCan('merchants.manage');
  const events = useQuery('platformAudit', { limit: 6 }, { select: (r) => r.data, enabled: canAudit });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Platform dashboard"
        subtitle="All merchants, stores and platform health"
        actions={canManage ? <Button onClick={() => nav('/merchants/new')}>Register merchant</Button> : null}
      />

      <QueryState query={stats}>
        {(s) => {
          const byStatus = s.merchantsByStatus || {};
          const total = Object.values(byStatus).reduce((a, b) => a + b, 0);
          const o = overview.data;
          return (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              <StatCard label="Total merchants" value={total} hint="registered" />
              <StatCard label="Live stores" value={byStatus.active || 0} hint="status active" />
              <StatCard label="Need attention" value={(byStatus.provisioning_failed || 0) + (byStatus.suspended || 0)} hint="failed or suspended" />
              <StatCard label="MRR" value={formatINR(s.mrrPaise)} hint="billed to active stores" />
              <StatCard label={`GMV (${o ? o.month_label : '…'})`} value={o ? formatINRCompact(o.gmv_month_paise) : '…'} hint="paid orders, all stores" />
              <StatCard label={`Orders (${o ? o.month_label : '…'})`} value={o ? o.orders_month : '…'} hint="paid this month" />
            </div>
          );
        }}
      </QueryState>

      <div className="grid gap-4 lg:grid-cols-3">
        <SectionCard title="GMV & billed MRR · last 6 months" className="lg:col-span-2">
          <QueryState query={overview}>
            {(o) => (
              <div className="grid gap-6 sm:grid-cols-2">
                {o.unreachable.length ? <Alert variant="warning" className="sm:col-span-2" title="Some stores could not be read">{o.unreachable.join(', ')}: their orders are missing from these totals.</Alert> : null}
                <AreaChart
                  title="Marketplace GMV by month"
                  description={`GMV grew from ${formatINRCompact(o.trend[0].gmv_paise)} in ${o.trend[0].month} to ${formatINRCompact(o.trend[o.trend.length - 1].gmv_paise)} in ${o.trend[o.trend.length - 1].month}.`}
                  data={o.trend}
                  index="month"
                  categories={['gmv_paise']}
                  valueFormatter={formatINRCompact}
                  showLegend={false}
                  yAxisWidth={64}
                  height={240}
                />
                <BarChart
                  title="Subscription revenue billed by month"
                  data={o.trend}
                  index="month"
                  categories={['mrr_paise']}
                  colors={['success']}
                  valueFormatter={formatINRCompact}
                  showLegend={false}
                  yAxisWidth={64}
                  height={240}
                />
              </div>
            )}
          </QueryState>
        </SectionCard>

        <SectionCard title="Merchants by plan">
          <QueryState query={stats}>
            {(s) => {
              const rows = shares(s.merchantsByPlan);
              return rows.length ? (
                <div className="space-y-4">
                  <DonutChart
                    title="Merchants by plan"
                    description={rows.map((r) => `${humanize(r.name)}: ${r.value}`).join(', ')}
                    data={rows.map((r) => ({ name: humanize(r.name), value: r.value }))}
                    centerLabel={<span className="text-xl font-bold">{rows.reduce((a, r) => a + r.value, 0)}</span>}
                    height={220}
                  />
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-[var(--app-border)]">
                    {Object.entries(s.merchantsByStatus || {}).map(([k, n]) => (
                      <span key={k} className="flex items-center gap-1 text-sm"><StatusBadge status={k} /> {n}</span>
                    ))}
                  </div>
                </div>
              ) : <p className="text-sm opacity-70">No merchants yet.</p>;
            }}
          </QueryState>
        </SectionCard>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <SectionCard title="Recent merchants" actions={<Button size="sm" variant="ghost" onClick={() => nav('/merchants')}>View all</Button>} className="lg:col-span-2">
          <QueryState query={merchants}>
            {(rows) => (rows.length ? (
              <DataTable
                data={rows.slice(0, 5)}
                onRowClick={(r) => nav(`/merchants/${r.id}`)}
                columns={[
                  { key: 'display_name', label: 'Merchant', render: (r) => (
                    <div className="min-w-0">
                      <div className="font-medium">{r.display_name}</div>
                      <div className="text-xs opacity-60 break-all">{r.primary_host || '—'}</div>
                    </div>
                  ) },
                  { key: 'plan_key', label: 'Plan', render: (r) => <Badge variant="primary">{r.plan_key || '—'}</Badge> },
                  { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
                  { key: 'created_at', label: 'Joined', hideOnMobile: true, render: (r) => formatDate(r.created_at) },
                ]}
              />
            ) : (
              <EmptyState title="No merchants yet" action={canManage ? <Button onClick={() => nav('/merchants/new')}>Register the first merchant</Button> : null} />
            ))}
          </QueryState>
        </SectionCard>

        <SectionCard title="Recent platform events" actions={canAudit ? <Button size="sm" variant="ghost" onClick={() => nav('/audit')}>Audit log</Button> : null}>
          {!canAudit ? <p className="text-sm opacity-70">The audit log needs the audit.view permission.</p> : (
          <QueryState query={events}>
            {(rows) => (
              <ul className="divide-y divide-[var(--app-border)]">
                {rows.map((e) => (
                  <li key={e.id} className="py-2 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{humanize(e.action)}</span>
                      <span className="text-xs opacity-60 shrink-0">{formatDateTime(e.at)}</span>
                    </div>
                    <div className="text-xs opacity-70 break-all">{e.target} · {e.actor}</div>
                  </li>
                ))}
              </ul>
            )}
          </QueryState>
          )}
        </SectionCard>
      </div>

      <SectionCard title="System health" actions={overview.data ? <span className="text-xs opacity-60">as of {formatDateTime(overview.data.as_of)}</span> : null}>
        <QueryState query={overview}>
          {(o) => (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {o.health.map((h) => (
                <div key={h.key} className="rounded-md border border-[var(--app-border)] p-3 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm opacity-70">{h.label}</span>
                    <Badge variant={HEALTH_VARIANT[h.status] || 'default'} size="sm">{h.status}</Badge>
                  </div>
                  <div className="text-lg font-semibold">{h.value}</div>
                  <div className="text-xs opacity-60">{h.hint}</div>
                </div>
              ))}
            </div>
          )}
        </QueryState>
      </SectionCard>

    </div>
  );
}
